import { useEffect, useMemo, useState } from 'react';

import Icon from './Icon.jsx';
import CharacterPortrait from './CharacterPortrait.jsx';
import { CHARACTERS, CHARACTER_GROUPS } from '../data/characters.js';

/**
 * The people of the Bible.
 *
 * A grid of portraits that opens into a profile: the portrait on the left, the
 * writing on the right, laid out as a reading column rather than a spec sheet.
 * On a phone the portrait sits above the text, because a 120px-wide column of
 * prose is unreadable.
 *
 * The profile is deliberately not a summary. It is three specific scenes, one
 * verse, the thing they got wrong, and what they left behind — because that is
 * what makes a person memorable, and a list of offices does not.
 */
export default function CharactersScreen({ onAsk, onOpenVerse }) {
  const [openId, setOpenId] = useState(null);
  const [group, setGroup] = useState('all');

  const character = openId ? CHARACTERS.find((c) => c.id === openId) : null;

  const shown = useMemo(
    () => (group === 'all' ? CHARACTERS : CHARACTERS.filter((c) => c.group === group)),
    [group],
  );

  // A newly opened profile starts at the top, not wherever the grid was.
  useEffect(() => {
    if (openId) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [openId]);

  if (character) {
    return (
      <section className="screen">
        <div className="run-bar">
          <button type="button" className="icon-btn icon-btn--sm" onClick={() => setOpenId(null)}>
            <Icon name="arrow" size={15} className="icon--flip" />
            People
          </button>
          <span className="run-bar__count">{character.era}</span>
        </div>

        <article className="profile">
          <div className="profile__figure">
            <CharacterPortrait portrait={character.portrait} size={200} />
            <span className="profile__emblem-note">{character.portrait.emblem}</span>
          </div>

          <div className="profile__body">
            <header className="profile__head">
              <span className="profile__group">{character.group}</span>
              <h2>{character.name}</h2>
              <p className="profile__epithet">{character.epithet}</p>
              {character.alsoKnownAs ? (
                <p className="profile__aka">Also called {character.alsoKnownAs}</p>
              ) : null}
            </header>

            <p className="profile__summary">{character.summary}</p>

            <div className="profile__verse">
              <blockquote className="scripture">{character.verse.text}</blockquote>
              <cite className="citation">{character.verse.ref}</cite>
            </div>

            <ol className="moments">
              {character.moments.map((moment, i) => (
                <li className="moment" key={moment.ref + i}>
                  <span className="moment__n" aria-hidden="true">
                    {i + 1}
                  </span>
                  <div>
                    <h3>{moment.title}</h3>
                    <p>{moment.text}</p>
                    <span className="moment__ref">{moment.ref}</span>
                  </div>
                </li>
              ))}
            </ol>

            <div className="profile__facets">
              <div className="facet facet--flaw">
                <span className="facet__label">The flaw</span>
                <p>{character.flaw}</p>
              </div>
              <div className="facet">
                <span className="facet__label">What they left</span>
                <p>{character.legacy}</p>
              </div>
            </div>

            <div className="profile__actions">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => onAsk?.({ ref: character.verse.ref, text: character.verse.text })}
              >
                <Icon name="dove" size={16} />
                Ask about {character.name.split(' ')[0]}
              </button>
            </div>
          </div>
        </article>
      </section>
    );
  }

  return (
    <section className="screen">
      <div className="screen__heading">
        <h2>People</h2>
        <p>{CHARACTERS.length} of the figures the story turns on.</p>
      </div>

      <div className="chips" role="tablist" aria-label="Filter by group">
        <button
          type="button"
          role="tab"
          aria-selected={group === 'all'}
          className={`chip${group === 'all' ? ' is-on' : ''}`}
          onClick={() => setGroup('all')}
        >
          Everyone
        </button>
        {CHARACTER_GROUPS.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={group === name}
            className={`chip${group === name ? ' is-on' : ''}`}
            onClick={() => setGroup(name)}
          >
            {name}
          </button>
        ))}
      </div>

      <ul className="people">
        {shown.map((person) => (
          <li key={person.id}>
            <button type="button" className="person" onClick={() => setOpenId(person.id)}>
              <CharacterPortrait portrait={person.portrait} size={92} />
              <span className="person__name">{person.name}</span>
              <span className="person__epithet">{person.epithet}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
